import React, { useEffect, useRef } from "react"
import TaskCreation from "./taskCreation/TaskCreation.controller"
import TimeReport from "./timeReport/TimeReport.controller"
import {
  getFirstWorkDayByCanSaveChanges,
  getLastWorkDayFromDate,
  getWorkDayAtDate,
} from "../../../services/workDay.service"
import useSessionContext from "../../../hooks/contextConsumers/useSessionContext.hook"
import TimeSummary from "./timeSummary/TimeSummary.controller"
import useTimeRegisterContext from "../hooks/useTimeRegisterContext.hook"
import UncompleteDayDialog from "./dayAlerts/UncompleteDayDialog.controller"
import MissingDayDialog from "./dayAlerts/MissingDayDialog.controller"
import { getTeamMemberVacationDayOffsInRange } from "../../../services/vacation.service"
import {
  getDateTime,
  getModifiedDateTime,
  isSameDay,
} from "../../../utils/dateTime.utils"

const TimeRegisterContent = ({ refreshProjects }) => {
  const {
    doFetchTasks,
    setDoFetchTasks,
    setIsDayFinished,
    setIsDayEditable,
    setShowUncompleteDayDialog,
    setShowMissingDayDialog,
    selectedDate,
    setSelectedDate,
    setUncompleteDay,
    setLastWorkDay,
    setCanSaveChanges,
    setLoading,
    workDayToDisplay,
    setWorkDayToDisplay,
    editingMissingDay,
    setEditingMissingDay,
    uncompleteDay,
    setDaysOff,
  } = useTimeRegisterContext()
  const { user } = useSessionContext()
  const dropdownProjectFilterRef = useRef(null)

  useEffect(() => {
    const selectedDateDateTime = getDateTime(selectedDate)
    const hasWorkDayToDisplayDate = Boolean(workDayToDisplay?.date)
    const workDayDateTime =
      hasWorkDayToDisplayDate ? getDateTime(workDayToDisplay?.date) : null
    const sameSelectedAndWorkDayDate =
      hasWorkDayToDisplayDate ?
        isSameDay(selectedDateDateTime, workDayDateTime)
      : false
    const shouldFetchWorkDayToDisplay =
      !uncompleteDay &&
      !editingMissingDay &&
      (!hasWorkDayToDisplayDate || !sameSelectedAndWorkDayDate)

    if (shouldFetchWorkDayToDisplay) {
      getWorkDayToDisplay()
    }
  }, [selectedDate])

  const getDayOffs = async () => {
    try {
      const firstDayOfCurrentWeek = getModifiedDateTime({
        dateTime: selectedDate,
        zone: "utc",
        startOf: "week",
      })
      const lastDayOfCurrentWeek = getModifiedDateTime({
        dateTime: selectedDate,
        zone: "utc",
        endOf: "week",
      })
      const response = await getTeamMemberVacationDayOffsInRange(
        firstDayOfCurrentWeek.minus({ months: 2 }).toISODate(),
        lastDayOfCurrentWeek.plus({ months: 2 }).toISODate()
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      const dayOffs = response?.result
      if (!dayOffs) {
        setDaysOff([])
        return []
      }
      const formattedDaysOff = dayOffs.map((day) => day?.split("T")[0])
      const saturday = firstDayOfCurrentWeek.plus({ days: 5 }).toISODate()
      const sunday = firstDayOfCurrentWeek.plus({ days: 6 }).toISODate()
      const allDaysOff = [...new Set([...formattedDaysOff, saturday, sunday])]
      setDaysOff(allDaysOff)
      return allDaysOff
    } catch (error) {
      setDaysOff([])
      return []
    }
  }

  const getUncompleteDay = async () => {
    const responseWorkDayThatCanSaveChanges =
      await getFirstWorkDayByCanSaveChanges(user.teamMemberID)
    const workDayThatCanSaveChanges = responseWorkDayThatCanSaveChanges?.result
    const dateOfWorkDayThatCanSaveChanges = getDateTime(
      workDayThatCanSaveChanges?.date?.split("T")[0]
    )

    if (
      workDayThatCanSaveChanges &&
      !isSameDay(dateOfWorkDayThatCanSaveChanges, getDateTime(selectedDate)) &&
      (workDayThatCanSaveChanges.isFinished ||
        dateOfWorkDayThatCanSaveChanges.toISODate() !==
          getDateTime().toISODate())
    ) {
      setShowUncompleteDayDialog(true)
      setSelectedDate(dateOfWorkDayThatCanSaveChanges.toJSDate())
      setWorkDayToDisplay(workDayThatCanSaveChanges)
      setCanSaveChanges(true)
      setLastWorkDay(workDayThatCanSaveChanges)
      setUncompleteDay(workDayThatCanSaveChanges)
      return true
    }
    return false
  }

  const getMissingDay = async (daysOff) => {
    let missingDayDate = false
    // 3. Dia sin iniciar que no sea hoy
    const firstDayOfCurrentWeek = getModifiedDateTime({ startOf: "week" })
    const todayDate = getDateTime()

    const monday = firstDayOfCurrentWeek.toJSDate()
    const todayIsMonday = isSameDay(todayDate, monday)
    const selectedDateIsMonday = isSameDay(monday, selectedDate)

    if (!todayIsMonday && !selectedDateIsMonday) {
      const responseLastWorkDayOfCurrentWeek = await getLastWorkDayFromDate(
        user.teamMemberID,
        firstDayOfCurrentWeek.toISODate()
      )
      const lastWorkDayOfCurrentWeek = responseLastWorkDayOfCurrentWeek?.result
      const dateOfLastWorkDayCurrentWeek = getDateTime(
        lastWorkDayOfCurrentWeek?.date?.split("T")[0]
      )
      if (lastWorkDayOfCurrentWeek) {
        missingDayDate = dateOfLastWorkDayCurrentWeek?.plus({ days: 1 })
      } else {
        missingDayDate = firstDayOfCurrentWeek
      }
      const dateOfYesterday = todayDate.minus({ days: 1 })
      if (
        !lastWorkDayOfCurrentWeek ||
        dateOfLastWorkDayCurrentWeek.startOf("day") <
          dateOfYesterday.startOf("day")
      ) {
        while (
          daysOff.includes(missingDayDate.toISODate()) && // Skip vacation days
          !isSameDay(missingDayDate, todayDate) // Skip today
        ) {
          missingDayDate = missingDayDate.plus({ days: 1 })
        }
        if (!isSameDay(missingDayDate, todayDate)) {
          setEditingMissingDay(true)
          setShowMissingDayDialog(true)
          setSelectedDate(missingDayDate.toJSDate())
          return true
        }
      }
    }
    return false
  }
  const getWorkDayToDisplay = async () => {
    setLoading(true)
    try {
      // 0. Dia dentro de la semana actual
      const daysOff = await getDayOffs()
      if (
        getDateTime(selectedDate) >= getModifiedDateTime({ startOf: "week" })
      ) {
        const uncompleteDay = await getUncompleteDay(daysOff)
        if (!uncompleteDay) {
          const missingDay = await getMissingDay(daysOff)
          if (!missingDay) {
            // 4. Dia seleccionado
            // 5. Dia actual
            const responseWorkDayOfSelectedDate = await getWorkDayAtDate(
              user.teamMemberID,
              selectedDate
            )
            const workDayOfSelectedDate = responseWorkDayOfSelectedDate?.result
            const isDayFinished = workDayOfSelectedDate?.isFinished === 1

            setIsDayFinished(isDayFinished)
            setLastWorkDay(workDayOfSelectedDate)
            setWorkDayToDisplay(workDayOfSelectedDate)
          }
        }
      } else {
        setUncompleteDay(null)
        setLastWorkDay(null)
        setWorkDayToDisplay(null)
        setIsDayFinished(true)
        setIsDayEditable(false)
      }
    } catch (error) {
      setLastWorkDay(null)
      setIsDayFinished(false)
      setIsDayEditable(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <TaskCreation
        refreshProjects={refreshProjects}
        dropdownProjectFilterRef={dropdownProjectFilterRef}
      />
      <TimeReport
        onClickBeginEditingNewTaskName={() => {
          setTimeout(() => {
            dropdownProjectFilterRef.current?.show()
          })
        }}
      />
      <TimeSummary setRefresh={() => setDoFetchTasks(!doFetchTasks)} />
      <UncompleteDayDialog />
      <MissingDayDialog />
    </>
  )
}

export default TimeRegisterContent
